import PropTypes from "prop-types";

import { Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import {
  formatNumber,
  getShortReference,
  getTransactionPath,
} from "@/features/shared";

/**
 * Component for displaying multiple downstream transaction references as clickable links.
 * Used for showing child/downstream transactions (e.g., a PO showing all GRs that processed it).
 * Each item is displayed on a new line.
 * Supports multiple references and automatically generates the correct route path.
 *
 * @param {Array} items - Array of objects containing referenceNumber and optional quantity
 * @param {boolean} shortReference - Whether to display shortened reference numbers (default: true)
 * @param {boolean} clickable - Whether the links should be clickable (default: true)
 * @returns Rendered links or "-" if no items
 *
 * @example
 * // Display goods receipts with quantities
 * <DownstreamTransactionLinks
 *   items={[
 *     { referenceNumber: "GR-20251007-ABC123", quantity: 5 },
 *     { referenceNumber: "GR-20251007-XYZ789", quantity: 3 }
 *   ]}
 *   clickable={hasPermission(PermissionEnum.READ_GOODS_RECEIPTS)}
 * />
 * // Output: ABC123 (5)
 * //         XYZ789 (3)
 *
 * @example
 * // Display without quantities
 * <DownstreamTransactionLinks items={[
 *   { referenceNumber: "PI-20251007-ABC123" }
 * ]} />
 * // Output: ABC123
 */
export const DownstreamTransactionLinks = ({
  items,
  shortReference = true,
  clickable = true,
}) => {
  if (!items || items.length === 0) return "-";

  return (
    <Stack direction="column" spacing={0.5}>
      {items.map((item) => {
        const path = getTransactionPath(item.referenceNumber);
        if (!path) return null;

        const displayText = shortReference
          ? getShortReference(item.referenceNumber)
          : item.referenceNumber;

        const linkText =
          item.quantity != null
            ? `${displayText} (${formatNumber(item.quantity)})`
            : displayText;

        // If not clickable, render as plain text
        if (!clickable) {
          return (
            <Typography
              key={item.referenceNumber}
              variant="body2"
              component="span"
            >
              {linkText}
            </Typography>
          );
        }

        return (
          <Link
            key={item.referenceNumber}
            component={RouterLink}
            to={path}
            underline="hover"
            sx={{ fontWeight: 500 }}
          >
            {linkText}
          </Link>
        );
      })}
    </Stack>
  );
};

DownstreamTransactionLinks.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      referenceNumber: PropTypes.string.isRequired,
      quantity: PropTypes.number,
    })
  ),
  shortReference: PropTypes.bool,
  clickable: PropTypes.bool,
};
